// Routines client — scheduled jobs that spawn refresh tasks.
//
// Lifted out of `../api` so the file boundary matches the backend
// `src/railyard/routes/routines.py`.

import type { components } from "@/lib/api-types";

import {
  API,
  requestJson,
  requestOk,
} from "../api";


type Schemas = components["schemas"];


// Generated from routes/routines.py:Routine. The hand-rolled version
// was missing `created_at`, `updated_at`, and `owner_user_id` (all
// present on the Routine dataclass and emitted by list/create/update).
// Schedule is narrowed to the Literal enum the server enforces via
// VALID_SCHEDULES.
export type RoutineSummary = Schemas["Routine"];
export type RoutineRun = Schemas["RoutineRun"];

export type BriefingRoutineSettings = {
  additional_topics?: string[];
  include_calendar?: boolean;
  include_email_triage?: boolean;
};

export async function listRoutines(): Promise<RoutineSummary[]> {
  return requestJson(`${API}/api/routines`);
}

export async function createRoutine(data: {
  title: string;
  description: string;
  prompt: string;
  schedule: string;
  schedule_time: string;
  run_at?: string | null;
  project_id?: string | null;
  // IANA timezone (ENG-3796). Omit to default to the caller's own
  // `User.timezone`, if set; pass `null` explicitly to force UTC.
  timezone?: string | null;
  // Per-routine model pin (ENG-3800). Omit to let the server write
  // DEFAULT_ROUTINE_MODEL (Sonnet, ENG-5323). Pass `null` to store no
  // pin (the fire path then inherits the Opus verification default).
  model?: string | null;
  settings?: BriefingRoutineSettings;
}): Promise<RoutineSummary> {
  return requestJson(`${API}/api/routines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updateRoutine(
  id: string,
  data: Partial<{
    title: string;
    description: string;
    prompt: string;
    schedule: string;
    schedule_time: string;
    run_at: string | null;
    enabled: boolean;
    project_id: string | null;
    timezone: string | null;
    // Same pin as create. `null` clears it (ENG-3800); omitting leaves
    // the stored value untouched.
    model: string | null;
    settings: BriefingRoutineSettings;
  }>
): Promise<RoutineSummary> {
  return requestJson(`${API}/api/routines/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function deleteRoutine(id: string): Promise<void> {
  await requestOk(`${API}/api/routines/${id}`, { method: "DELETE" });
}

export async function fireRoutine(id: string): Promise<{ task_id: string }> {
  return requestJson(`${API}/api/routines/${id}/fire`, { method: "POST" });
}
